import { cva, type VariantProps } from 'class-variance-authority';
import { forwardRef, type ComponentPropsWithoutRef, type ComponentRef } from 'react';
import { cn } from '@r/utils/utils.ts';
import * as AccordionPrimitive from '@radix-ui/react-accordion';
import { ChevronDown } from 'lucide-react';

//==============================================================================// ACCORDION

const Accordion = AccordionPrimitive.Root;

//==============================================================================// ACCORDION ITEM

const accordionItemVariants = cva('', {
  variants: {
    variant: {
      default: 'border-b',
      menu: 'rounded-lg text-foreground hover:bg-muted'
    },
    size: {
      default: ''
    }
  },
  defaultVariants: {
    variant: 'default',
    size: 'default'
  }
});

export interface AccordionItemProps
  extends ComponentPropsWithoutRef<typeof AccordionPrimitive.Item>,
    VariantProps<typeof accordionItemVariants> {
  className?: string;
  variant?: 'default' | 'menu';
  size?: 'default';
}

const AccordionItem = forwardRef<ComponentRef<typeof AccordionPrimitive.Item>, AccordionItemProps>(
  ({ className, variant, size, ...props }, ref) => (
    <AccordionPrimitive.Item
      ref={ref}
      className={cn(accordionItemVariants({ variant, size, className }))}
      {...props}
    />
  )
);

AccordionItem.displayName = 'AccordionItem';

//==============================================================================// ACCORDION TRIGGER

const accordionTriggerVariants = cva(
  'menu-item flex flex-1 items-center justify-between transition-all [&[data-state=open]>svg]:rotate-180 disabled:opacity-30 disabled:pointer-events-none',
  {
    variants: {
      variant: {
        default: 'hover:underline',
        menu: 'rounded-lg text-foreground'
      },
      size: {
        default: 'py-4 font-medium',
        menu: 'text-[1rem] h-9 px-2'
      }
    },
    defaultVariants: {
      variant: 'default',
      size: 'default'
    }
  }
);

export interface AccordionTriggerProps
  extends ComponentPropsWithoutRef<typeof AccordionPrimitive.Trigger>,
    VariantProps<typeof accordionTriggerVariants> {
  className?: string;
  variant?: 'default' | 'menu';
  size?: 'default' | 'menu';
}

const AccordionTrigger = forwardRef<
  ComponentRef<typeof AccordionPrimitive.Trigger>,
  AccordionTriggerProps
>(({ className, variant, size, children, ...props }, ref) => (
  <div className="menu-item-container">
    <AccordionPrimitive.Header className="flex">
      <AccordionPrimitive.Trigger
        ref={ref}
        className={cn(accordionTriggerVariants({ variant, size, className }))}
        {...props}
      >
        {children}
        <ChevronDown className="w-4 h-4 transition-transform duration-200 shrink-0" />
      </AccordionPrimitive.Trigger>
    </AccordionPrimitive.Header>
  </div>
));

AccordionTrigger.displayName = AccordionPrimitive.Trigger.displayName;

//==============================================================================// ACCORDION CONTENT

const accordionContentVariants = cva('CollapsibleContent flex flex-col items-center', {
  variants: {
    size: {
      default: 'pb-4 pt-0',
      menu: 'pb-[0.2rem] px-2 pb-2'
    }
  },
  defaultVariants: {
    size: 'default'
  }
});

export interface AccordionContentProps
  extends ComponentPropsWithoutRef<typeof AccordionPrimitive.Content>,
    VariantProps<typeof accordionContentVariants> {
  className?: string;
  size?: 'default' | 'menu';
}

const AccordionContent = forwardRef<
  ComponentRef<typeof AccordionPrimitive.Content>,
  AccordionContentProps
>(({ className, size, children, ...props }, ref) => (
  <AccordionPrimitive.Content
    ref={ref}
    className="overflow-hidden text-sm transition-all data-[state=closed]:animate-accordion-up data-[state=open]:animate-accordion-down"
    {...props}
  >
    <div className={cn(accordionContentVariants({ size, className }))}>{children}</div>
  </AccordionPrimitive.Content>
));

AccordionContent.displayName = AccordionPrimitive.Content.displayName;

export { Accordion, AccordionItem, AccordionTrigger, AccordionContent };
